import type { MetricsOverviewDto } from '@planuze/admin-contracts';
import { Skeleton } from '@planuze/ui';
import {
  DollarSign,
  Lock,
  type LucideIcon,
  Monitor,
  TrendingDown,
  UserPlus,
  Users,
} from 'lucide-react';
import {
  type MetricsWidgetsContent,
  useMetricsWidgetsContent,
} from '../../content/sections/metrics-widgets.js';
import { type Currency, fmtCurrency, fmtNumber, fmtPercent } from '../../lib/format.js';
import { MetricCard } from './metric-card.js';

/* Cada card do overview tem casa numa única aba (sem duplicação): a aba Receita mostra os
   KPIs de MRR/churn, Assinantes os de atividade/contas e Cadastros os de signups. */
type MetricSection = 'revenue' | 'subscribers' | 'signups';

interface CardSpec {
  readonly label: string;
  readonly value: string;
  readonly icon: LucideIcon;
  readonly hint?: string;
}

/* MRR colapsa em um card só (antes eram dois): a moeda vem do toggle page-level, então
   USD e BRL não coexistem mais como cards separados. */
function revenueCards(
  o: MetricsOverviewDto,
  currency: Currency,
  copy: MetricsWidgetsContent['metricCards'],
): readonly CardSpec[] {
  const mrr = currency === 'USD' ? o.mrrUsd : o.mrrBrl;
  return [
    {
      label: copy.mrr,
      value: fmtCurrency(mrr, currency),
      icon: DollarSign,
      hint: copy.mrrHint(currency),
    },
    { label: copy.churn30d, value: fmtPercent(o.churn30dPct), icon: TrendingDown },
  ];
}

function subscriberCards(
  o: MetricsOverviewDto,
  copy: MetricsWidgetsContent['metricCards'],
): readonly CardSpec[] {
  return [
    { label: copy.activeUsers24h, value: fmtNumber(o.activeUsers24h), icon: Users },
    { label: copy.activeDevices24h, value: fmtNumber(o.activeDevices24h), icon: Monitor },
    {
      label: copy.blockedOrgs,
      value: fmtNumber(o.blockedOrgs),
      hint: copy.blockedOrgsHint(fmtNumber(o.totalOrgs)),
      icon: Lock,
    },
  ];
}

function signupCards(
  o: MetricsOverviewDto,
  copy: MetricsWidgetsContent['metricCards'],
): readonly CardSpec[] {
  return [
    { label: copy.signups7d, value: fmtNumber(o.signups7d), icon: UserPlus },
    { label: copy.signups30d, value: fmtNumber(o.signups30d), icon: UserPlus },
  ];
}

function cardsFor(
  section: MetricSection,
  o: MetricsOverviewDto,
  currency: Currency,
  copy: MetricsWidgetsContent['metricCards'],
): readonly CardSpec[] {
  switch (section) {
    case 'revenue':
      return revenueCards(o, currency, copy);
    case 'subscribers':
      return subscriberCards(o, copy);
    case 'signups':
      return signupCards(o, copy);
  }
}

/* Nº de skeletons por seção — casa com o nº de cards derivados quando `overview` chega. */
const SECTION_SIZE: Readonly<Record<MetricSection, number>> = {
  revenue: 2,
  subscribers: 3,
  signups: 2,
};

interface Props {
  readonly overview: MetricsOverviewDto | undefined;
  readonly section: MetricSection;
  /** Só a seção `revenue` usa a moeda; default `BRL` (produto pt-BR-first). */
  readonly currency?: Currency;
}

/* Grade de cards derivada do overview de forma declarativa (§4.18), fatiada por aba. */
export function MetricCardsGrid({ overview, section, currency = 'BRL' }: Props) {
  const copy = useMetricsWidgetsContent().metricCards;
  if (overview === undefined) {
    return (
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {Array.from({ length: SECTION_SIZE[section] }, (_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
      {cardsFor(section, overview, currency, copy).map((card) => (
        <MetricCard key={card.label} {...card} />
      ))}
    </div>
  );
}
